import {
  Card,
  Checkbox,
  CrudMode,
  DataArray,
  DataDisplay,
  InfoBanner,
  pn,
  Stack,
  Typography,
  ValidationErrors,
} from "@compass/core";
import { Select } from "components";
import { LkRole, LkRoleId, LkUserDataAssignmentTypeId, User, UserEditResource } from "generated/openApi";
import produce from "immer";
import "moment-timezone";

export const DataAssignment = (props: {
  crudMode: CrudMode;
  appUserRoleId: number | undefined;
  user: User;
  setUser: (value: User) => void;
  editResource: UserEditResource;
  validationErrors: ValidationErrors | string[];
  selectedRole: LkRole | undefined;
}) => {
  const UNRESTRICTED: string = "Unrestricted (All)";

  return (
    <Card transparentHeader={true} header="Data Assignment" fullHeight>
      <Stack spacing="md">
        <InfoBanner title="">
          <Typography variant="caption" color="primary">
            Data can be configured for {props.selectedRole?.name} role to allow only access to specified Regions,
            Districts or Schools. Leave blank for unrestricted access to all data.
          </Typography>
        </InfoBanner>

        <Stack>
          {(props.crudMode === "view" || (props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator)) && (
            <DataDisplay>
              {props.user.dataAssignmentType?.id ? props.user.dataAssignmentType?.name : UNRESTRICTED}
            </DataDisplay>
          )}
          {(props.crudMode === "edit" &&
            <Select
              placeholder={UNRESTRICTED}
              disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
              value={props.editResource.userDataAssignmentTypes?.find((x) => x.id === props.user.dataAssignmentTypeId)}
              options={props.editResource.userDataAssignmentTypes}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) =>
                props.setUser(
                  produce(props.user, (draft) => {
                    draft.dataAssignmentTypeId = value?.id;
                  })
                )
              }
              errors={props.validationErrors[pn<User>("dataAssignmentTypeId")]}
            />
          )}
        </Stack>

        <Stack>
          {props.user.dataAssignmentTypeId === Number(LkUserDataAssignmentTypeId.Region) && (
            <>
              {props.crudMode === "view" && <DataArray value={props.user.regions?.map((x) => x.name)} />}

              {props.crudMode === "edit" &&
                props.editResource.regions?.map((x) => (
                  <Stack>
                    <Checkbox
                      key={x.id}
                      disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
                      checked={props.user.regions?.map((x) => x.id).includes(x.id)}
                      onClick={() =>
                        props.setUser(
                          produce(props.user, (draft) => {
                            const index: number | undefined = props.user.regions?.map((x) => x.id).indexOf(x.id);
                            if (index !== undefined && index >= 0) draft.regions?.splice(index, 1);
                            else draft.regions?.push(x);
                          })
                        )
                      }
                      label={x.name}
                    />
                  </Stack>
                ))}
            </>
          )}
        </Stack>
        <Stack>
          {props.user.dataAssignmentTypeId === Number(LkUserDataAssignmentTypeId.District) && (
            <>
              {props.crudMode === "view" && <DataArray value={props.user.districts?.map((x) => x.name)} />}

              {props.crudMode === "edit" &&
                props.editResource.districts?.map((x) => (
                  <Stack>
                    <Checkbox
                      key={x.id}
                      disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
                      checked={props.user.districts?.map((x) => x.id).includes(x.id)}
                      onClick={() =>
                        props.setUser(
                          produce(props.user, (draft) => {
                            const index: number | undefined = props.user.districts?.map((x) => x.id).indexOf(x.id);
                            if (index !== undefined && index >= 0) draft.districts?.splice(index, 1);
                            else draft.districts?.push(x);
                          })
                        )
                      }
                      label={x.name}
                    />
                  </Stack>
                ))}
            </>
          )}
        </Stack>
        <Stack>
          {props.user.dataAssignmentTypeId === Number(LkUserDataAssignmentTypeId.School) && (
            <>
              {props.crudMode === "view" && <DataArray value={props.user.schools?.map((x) => x.name)} />}

              {props.crudMode === "edit" &&
                props.editResource.schools?.map((x) => (
                  <Stack>
                    <Checkbox
                      key={x.id}
                      disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
                      checked={props.user.schools?.map((x) => x.id).includes(x.id)}
                      onClick={() =>
                        props.setUser(
                          produce(props.user, (draft) => {
                            const index: number | undefined = props.user.schools?.map((x) => x.id).indexOf(x.id);
                            if (index !== undefined && index >= 0) draft.schools?.splice(index, 1);
                            else draft.schools?.push(x);
                          })
                        )
                      }
                      label={x.name}
                    />
                  </Stack>
                ))}
            </>
          )}
        </Stack>
        <Stack>
          {props.user.dataAssignmentTypeId === Number(LkUserDataAssignmentTypeId.Program) && (
            <>
              {props.crudMode === "view" && <DataArray value={props.user.programs?.map((x) => x.name)} />}

              {props.crudMode === "edit" &&
                props.editResource.programs?.map((x) => (
                  <Stack>
                    <Checkbox
                      key={x.id}
                      disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
                      checked={props.user.programs?.map((x) => x.id).includes(x.id)}
                      onClick={() =>
                        props.setUser(
                          produce(props.user, (draft) => {
                            const index: number | undefined = props.user.programs?.map((x) => x.id).indexOf(x.id);
                            if (index !== undefined && index >= 0) draft.programs?.splice(index, 1);
                            else draft.programs?.push(x);
                          })
                        )
                      }
                      label={x.name}
                    />
                  </Stack>
                ))}
            </>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};
